import React, { useState, useEffect } from 'react';
import { http } from '../../lib/api';

export default function ImportarEquipos({ onImportComplete, onClose }) {
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const [selectedSucursal, setSelectedSucursal] = useState('');
  const [file, setFile] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [detectedInfo, setDetectedInfo] = useState(null);
  const [importType, setImportType] = useState('csv'); // 'csv' o 'glpi'
  const [previewData, setPreviewData] = useState(null); // Datos de vista previa
  const [showPreview, setShowPreview] = useState(false); // Mostrar vista previa
  const [confirmedFile, setConfirmedFile] = useState(null); // Archivo confirmado para importar

  useEffect(() => {
    cargarClientes();
  }, []);

  useEffect(() => {
    if (selectedCliente) {
      // Resetear la sucursal seleccionada cuando cambia el cliente
      setSelectedSucursal('');
      cargarSucursales(selectedCliente);
      
      // Debug info
      console.log('Cliente seleccionado:', selectedCliente);
      const clienteSeleccionado = clientes.find(c => c._id === selectedCliente);
      if (clienteSeleccionado) {
        console.log('Sucursales disponibles:', clienteSeleccionado.sucursales);
      }
    } else {
      setSucursales([]);
      setSelectedSucursal('');
    }
  }, [selectedCliente, clientes]);

  async function cargarClientes() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró el token');
        setStatus('❌ Error de autenticación');
        return;
      }

      const res = await http.get('/clientes', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.data) {
        console.error('No hay datos en la respuesta');
        setStatus('❌ Error al cargar clientes');
        return;
      }

      console.log('Clientes cargados:', res.data);
      setClientes(res.data);
    } catch (err) {
      console.error('Error cargando clientes:', err);
      setStatus('❌ Error al cargar clientes');
      setClientes([]);
    }
  }

  async function cargarSucursales(clienteId) {
    try {
      const token = localStorage.getItem('token');
      // Usar el endpoint específico para obtener sucursales
      const res = await http.get(`/clientes/${clienteId}/sucursales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (Array.isArray(res.data)) {
        console.log('Sucursales cargadas:', res.data);
        setSucursales(res.data);
      } else {
        console.warn('Formato inesperado de respuesta para sucursales');
        setSucursales([]);
      }
    } catch (err) {
      console.error('Error cargando sucursales', err);
      setStatus('❌ Error al cargar sucursales');
      setSucursales([]);
    }
  }

  const detectClienteFromContent = async (content) => {
    try {
      if (!content) {
        console.warn('No hay contenido para analizar');
        return;
      }

      console.log('Analizando contenido CSV:', content.substring(0, 200) + '...');
      const lines = content.split(/[\r\n]+/);
      
      if (lines.length < 2) {
        console.warn('El CSV debe tener al menos dos líneas');
        return;
      }

      // La segunda línea contiene los datos
      const dataLine = lines[1];
      const fields = dataLine.split(';'); // GLPI usa punto y coma como separador

      if (fields.length < 2) {
        console.warn('No hay suficientes campos en la línea de datos');
        return;
      }

      const clienteField = fields[1].replace(/"/g, '').trim();
      console.log('Campo de cliente encontrado:', clienteField);

      if (!clientes || clientes.length === 0) {
        console.warn('No hay clientes cargados para comparar');
        return;
      }

      // Buscar el cliente que coincida con la entidad
      const clienteEncontrado = clientes.find(c => 
        c.nombre.toLowerCase() === clienteField.toLowerCase()
      );

      if (clienteEncontrado) {
        console.log('Cliente encontrado:', clienteEncontrado);
        setSelectedCliente(clienteEncontrado._id);
        
        // Si el cliente tiene una única sucursal, seleccionarla automáticamente
        if (clienteEncontrado.sucursales?.length === 1) {
          const suc = clienteEncontrado.sucursales[0];
          setSelectedSucursal(typeof suc === 'object' && suc !== null ? suc.nombre : suc);
        }
        
        setDetectedInfo({
          cliente: clienteEncontrado.nombre,
          entidad: clienteField
        });
      } else {
        console.warn('No se encontró coincidencia para el cliente:', clienteField);
      }
    } catch (err) {
      console.error('Error detectando cliente:', err);
    }
  };

  async function obtenerVistaPrevia() {
    if (!file && !csvText) {
      setStatus('❌ Por favor proporcione un archivo CSV de GLPI');
      return;
    }

    setLoading(true);
    setStatus('Generando vista previa...');

    try {
      const formData = new FormData();
      
      if (file) {
        formData.append('file', file);
      } else if (csvText) {
        const blob = new Blob([csvText], { type: 'text/csv' });
        const csvFile = new File([blob], 'preview.csv', { type: 'text/csv' });
        formData.append('file', csvFile);
      }

      const token = localStorage.getItem('token');
      const res = await http.post('/equipos/preview-glpi', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setPreviewData(res.data);
      setShowPreview(true);
      setStatus('✅ Vista previa generada correctamente');
    } catch (err) {
      console.error('Error en la vista previa', err);
      setStatus(`❌ Error en la vista previa: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function confirmarImportacion() {
    if (!previewData) {
      setStatus('❌ No hay datos de vista previa');
      return;
    }

    setLoading(true);
    setStatus('Importando equipos...');

    try {
      const formData = new FormData();
      
      if (file) {
        formData.append('file', file);
      } else if (csvText) {
        const blob = new Blob([csvText], { type: 'text/csv' });
        const csvFile = new File([blob], 'import.csv', { type: 'text/csv' });
        formData.append('file', csvFile);
      }
      // Enviar sucursal seleccionada si existe
      if (selectedSucursal) {
        formData.append('sucursal', selectedSucursal);
      }
      const token = localStorage.getItem('token');
      const res = await http.post('/equipos/import-glpi', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setStatus(`✅ ${res.data.mensaje}`);
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      console.error('Error en la importación', err);
      setStatus(`❌ Error en la importación: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Para GLPI, primero mostrar vista previa
    if (importType === 'glpi') {
      if (showPreview) {
        // Si ya se mostró la vista previa, confirmar importación
        await confirmarImportacion();
      } else {
        // Primera vez, mostrar vista previa
        await obtenerVistaPrevia();
      }
      return;
    }
    
    // Flujo original para CSV estándar
    // Validaciones diferentes según el tipo de importación
    if (!selectedCliente || !selectedSucursal || (!file && !csvText)) {
      setStatus('❌ Por favor complete todos los campos y proporcione un archivo o texto CSV');
      return;
    }

    setLoading(true);
    setStatus('Importando equipos...');

    try {
      const formData = new FormData();
      
      // Solo agregar cliente_id si no es importación GLPI (se detecta automáticamente)
      if (importType === 'csv') {
        formData.append('cliente_id', selectedCliente);
        formData.append('sucursal', selectedSucursal);
      }

      if (file) {
        formData.append('file', file);
      } else if (csvText) {
        // Crear un archivo Blob a partir del texto CSV
        const blob = new Blob([csvText], { type: 'text/csv' });
        const csvFile = new File([blob], 'import.csv', { type: 'text/csv' });
        formData.append('file', csvFile);
      }

      const token = localStorage.getItem('token');
      
      // Seleccionar endpoint según el tipo de importación
      const endpoint = importType === 'glpi' ? '/equipos/import-glpi' : '/equipos/import-csv';
      
      const res = await http.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setStatus(`✅ ${res.data.mensaje}`);
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      console.error('Error en la importación', err);
      setStatus(`❌ Error en la importación: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-[500px] max-w-[96vw]">
        <h3 className="text-lg font-semibold mb-4">Importar Equipos</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Selección del tipo de importación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de importación
            </label>
            <select
              value={importType}
              onChange={(e) => setImportType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="csv">CSV Estándar (requiere sucursal)</option>
              <option value="glpi">GLPI Export (detecta automáticamente)</option>
            </select>
          </div>

          {/* Selección de Cliente */}
          {/* Selección de Cliente - Solo para CSV estándar */}
          {importType === 'csv' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cliente
            </label>
            <select
              id="cliente"
              name="cliente"
              className="w-full border rounded p-2"
              value={selectedCliente}
              onChange={(e) => setSelectedCliente(e.target.value)}
              disabled={loading}
              required
            >
              <option value="">Seleccione un cliente</option>
              {Array.isArray(clientes) && clientes.map((cliente) => (
                <option key={cliente._id} value={cliente._id}>
                  {cliente.nombre}
                </option>
              ))}
            </select>
          </div>
          )}

          {/* Selección de Sucursal - Solo para CSV estándar */}
          {importType === 'csv' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal
              </label>
              <select
                id="sucursal"
                name="sucursal"
                className="w-full border rounded p-2"
                value={selectedSucursal}
              onChange={(e) => setSelectedSucursal(e.target.value)}
              disabled={!selectedCliente || loading}
              required
            >
              <option value="">Seleccione una sucursal</option>
              {Array.isArray(sucursales) && sucursales.length > 0 ? (
                sucursales.map((sucursal, index) => (
                  <option key={`sucursal-${index}`} value={sucursal}>
                    {sucursal}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  {selectedCliente ? "Este cliente no tiene sucursales definidas" : "Primero seleccione un cliente"}
                </option>
              )}
            </select>
            </div>
          )}

          {/* Selección de Archivo o Texto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {importType === 'glpi' ? 'Archivo CSV de GLPI' : 'Archivo CSV o pegar texto'}
            </label>
            <div className="space-y-2">
              {/* Información del formato */}
              {importType === 'glpi' && (
                <div className="bg-emerald-50 p-3 rounded-md text-sm text-emerald-800">
                  <strong>Formato GLPI:</strong> Exporta desde GLPI con delimitador punto y coma (;). 
                  El cliente se detecta automáticamente desde el campo "Entidad" (ej: "Elements-GLPICPR" → "CPR").
                  <br />
                  <strong>Campos incluidos:</strong> Nombre, Entidad, Fabricantes, Número de serie, Tipo, Modelo, Sistema operativo, Procesadores, Memoria, etc.
                </div>
              )}
              
              <input
                id="csv-file"
                name="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => {
                  const selectedFile = e.target.files[0];
                  setFile(selectedFile);
                  setCsvText(''); // Limpiar el texto si se selecciona un archivo
                  
                  // Leer el contenido del archivo para detectar el cliente
                  if (selectedFile) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      const content = e.target.result;
                      detectClienteFromContent(content);
                    };
                    reader.readAsText(selectedFile);
                  }
                }}
                className="w-full border rounded p-2"
                disabled={loading || csvText}
              />
              <textarea
                id="csv-text"
                name="csv-text"
                placeholder="O pega el contenido CSV aquí..."
                value={csvText}
                onChange={(e) => {
                  const text = e.target.value;
                  setCsvText(text);
                  setFile(null); // Limpiar el archivo si se pega texto
                  detectClienteFromContent(text);
                }}
                className="w-full border rounded p-2 min-h-[100px]"
                disabled={loading || file}
              />
            </div>
          </div>

          {/* Información detectada */}
          {detectedInfo && (
            <div className="text-sm p-2 rounded bg-emerald-100 text-emerald-700 mb-2">
                Se detectó la entidad "{detectedInfo.entidad}" que corresponde al cliente "{detectedInfo.cliente}"
            </div>
          )}

          {/* Estado de la importación */}
          {status && (
            <div className={`text-sm p-2 rounded ${
              status.startsWith('❌') ? 'bg-red-100 text-red-700' :
              status.startsWith('✅') ? 'bg-green-100 text-green-700' :
              'bg-emerald-100 text-emerald-700'
            }`}>
              {status}
            </div>
          )}

          {/* Vista previa de importación GLPI */}
          {showPreview && previewData && (
            <div className="border rounded-lg bg-gray-50 p-2 mb-2" style={{maxHeight: '40vh', overflowY: 'auto', fontSize: '0.95em'}}>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Vista previa de la importación
              </h3>
              
              {/* Información del cliente detectado */}
              <div className="bg-emerald-100 p-3 rounded-md mb-4">
                <h4 className="font-medium text-emerald-800">Cliente detectado:</h4>
                <p className="text-emerald-700">{typeof previewData.cliente_detectado === 'object' ? previewData.cliente_detectado?.nombre || '' : previewData.cliente_detectado}</p>
                <p className="text-sm text-emerald-600">Total de equipos: {previewData.total_filas}</p>
                  {/* Selección de sucursal si el cliente tiene sucursales */}
                  {previewData.cliente_detectado && typeof previewData.cliente_detectado === 'object' && Array.isArray(previewData.cliente_detectado.sucursales) && previewData.cliente_detectado.sucursales.length > 0 && (
                    <div className="mt-2">
                      <label className="block text-emerald-700 text-sm mb-1">Si pertenece a una sucursal asígnela aquí (Opcional):</label>
                      <select
                        className="border rounded px-2 py-1 w-full text-sm"
                        value={selectedSucursal || ''}
                        onChange={e => setSelectedSucursal(e.target.value)}
                      >
                        <option value="">Sin sucursal</option>
                        {previewData.cliente_detectado.sucursales.map((sucursal, idx) => (
                          <option key={`sucursal-preview-${idx}`} value={sucursal}>{sucursal}</option>
                        ))}
                      </select>
                    </div>
                  )}
              </div>

              {/* Advertencias */}
              {previewData.warnings && previewData.warnings.length > 0 && (
                <div className="bg-yellow-100 p-3 rounded-md mb-4">
                  <h4 className="font-medium text-yellow-800">Advertencias:</h4>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {previewData.warnings.map((warning, index) => (
                      <li key={typeof warning === 'string' ? warning : index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Vista previa de equipos (primeros 3) */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-800">
                  Vista previa de equipos (primeros {Math.min(3, previewData.equipos_preview.length)}):
                </h4>
                
                {previewData.equipos_preview.slice(0, 3).map((equipo, index) => (
                  <div key={equipo.datos_procesados && equipo.datos_procesados.nombre ? equipo.datos_procesados.nombre : index} className="border rounded bg-white p-3">
                    <h5 className="font-medium text-gray-700 mb-2">
                      Equipo {index + 1}: {equipo.datos_procesados.nombre}
                    </h5>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {/* Datos originales */}
                      <div>
                        <h6 className="font-medium text-gray-600 mb-1">Datos originales (GLPI):</h6>
                        <div className="bg-gray-100 p-2 rounded text-xs">
                          <div><strong>Entidad:</strong> {equipo.datos_originales.Entidad}</div>
                          <div><strong>Nombre:</strong> {equipo.datos_originales.Nombre}</div>
                          <div><strong>Fabricante:</strong> {equipo.datos_originales.Fabricantes}</div>
                          <div><strong>Modelo:</strong> {equipo.datos_originales.Modelo}</div>
                          <div><strong>S/N:</strong> {equipo.datos_originales['Número de serie']}</div>
                          <div><strong>Tipo:</strong> {equipo.datos_originales.Tipo}</div>
                          <div><strong>SO:</strong> {equipo.datos_originales['Sistema operativo']}</div>
                          <div><strong>Procesador:</strong> {equipo.datos_originales.Procesadores}</div>
                          <div><strong>Memoria:</strong> {equipo.datos_originales.Memoria}</div>
                        </div>
                      </div>
                      
                      {/* Datos procesados */}
                      <div>
                        <h6 className="font-medium text-gray-600 mb-1">Datos procesados (Elements):</h6>
                        <div className="bg-green-100 p-2 rounded text-xs">
                          <div><strong>Cliente:</strong> {equipo.datos_procesados.cliente}</div>
                          <div><strong>Nombre:</strong> {equipo.datos_procesados.nombre}</div>
                          <div><strong>Marca:</strong> {equipo.datos_procesados.marca}</div>
                          <div><strong>Modelo:</strong> {equipo.datos_procesados.modelo}</div>
                          <div><strong>Serie:</strong> {equipo.datos_procesados.numero_serie}</div>
                          <div><strong>Tipo:</strong> {equipo.datos_procesados.tipo}</div>
                          <div><strong>SO:</strong> {equipo.datos_procesados.sistema_operativo}</div>
                          <div><strong>Procesador:</strong> {equipo.datos_procesados.procesador}</div>
                          <div><strong>RAM:</strong> {equipo.datos_procesados.memoria_ram}</div>
                          <div><strong>Disco:</strong> {equipo.datos_procesados.disco}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {previewData.equipos_preview.length > 3 && (
                  <div className="text-center text-sm text-gray-600">
                    ... y {previewData.equipos_preview.length - 3} equipos más
                  </div>
                )}
              </div>

              {/* Botones de confirmación */}
              <div className="flex justify-end space-x-2 mt-4 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData(null);
                    setConfirmedFile(null);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  disabled={loading}
                >
                  Cancelar vista previa
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Cambiar el texto del botón principal y proceder
                    document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  disabled={loading}
                >
                  {loading ? 'Confirmando...' : 'Confirmar importación'}
                </button>
              </div>
            </div>
          )}

          {/* Botones */}
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              disabled={loading}
              style={{ display: showPreview ? 'none' : 'block' }}
            >
              {loading ? 'Procesando...' : 
               importType === 'glpi' && !showPreview ? 'Vista previa' : 'Importar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}